import type { ReactNode } from "react";
import { Button } from "antd";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/shared/lib/utils";

// Landing-page palette, sampled from the design (Landing Page.png).
export const NAVY = "#120240";
export const PINK = "#F18095";

export const SIGN_UP_PATH = "/patient/auth/signup";
export const SIGN_IN_PATH = "/patient/auth/signin";
export const CONTACT_EMAIL = "admin@medsync.com";

/** Centered content column. "wide" = 1200px sections, "narrow" = 1136px nav/hero. */
export function Container({
  children, className, size = "wide",
}: { children: ReactNode; className?: string; size?: "wide" | "narrow" }) {
  return (
    <div className={cn(
      "mx-auto w-full px-6 md:px-10",
      size === "wide" ? "max-w-[1280px]" : "max-w-[1216px]",
      className,
    )}>
      {children}
    </div>
  );
}

/** Small uppercase label with a leading dot, e.g. "• GETTING STARTED". */
export function Eyebrow({
  children, className, tone = "pink",
}: { children: ReactNode; className?: string; tone?: "pink" | "white" }) {
  return (
    <p className={cn(
      "flex items-center gap-1.5 text-[11px] font-bold uppercase leading-4 tracking-[0.12em]",
      tone === "pink" ? "text-[#F18095]" : "text-white",
      className,
    )}>
      <span className={cn("h-1 w-1 rounded-full", tone === "pink" ? "bg-[#F18095]" : "bg-white")} />
      {children}
    </p>
  );
}

/** Centered eyebrow + section heading block used by most sections. */
export function SectionHeading({
  eyebrow, title, subtitle, className,
}: { eyebrow: string; title: ReactNode; subtitle?: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col items-center text-center", className)}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="!mt-3 text-[32px] font-extrabold leading-[1.1] tracking-[-0.025em] text-[#120240] md:text-[40px] lg:text-[44px]">
        {title}
      </h2>
      {subtitle && (
        <p className="!mt-4 max-w-[490px] text-[17px] leading-7 text-[#4B5563]">{subtitle}</p>
      )}
    </div>
  );
}

/** Check bullet: soft tinted circle with a check, followed by text. */
export function CheckItem({
  children, tone = "pink", className,
}: { children: ReactNode; tone?: "pink" | "green" | "solid"; className?: string }) {
  const circle = {
    pink:  "h-[22px] w-[22px] bg-[#FDE4E9] text-[#F18095]",
    green: "h-5 w-5 bg-[#DCFCE7] text-[#16A34A]",
    solid: "h-5 w-5 bg-[#F18095] text-white",
  }[tone];
  return (
    <li className={cn("flex items-center gap-3", className)}>
      <span className={cn("flex shrink-0 items-center justify-center rounded-full", circle)}>
        <Icon icon="lucide:check" className="h-3 w-3" strokeWidth={3} />
      </span>
      <span>{children}</span>
    </li>
  );
}

/** Square tinted tile holding an icon. */
export function IconTile({
  icon, className, iconClassName,
}: { icon: string; className?: string; iconClassName?: string }) {
  return (
    <span className={cn("flex shrink-0 items-center justify-center", className)}>
      <Icon icon={icon} className={iconClassName} />
    </span>
  );
}

/** MedSync logo mark + wordmark. */
export function BrandLogo({ className }: { className?: string }) {
  return (
    <span className={cn("flex items-center gap-2.5", className)}>
      <img src="/logo-icon.png" alt="" aria-hidden="true" className="h-8 w-8 object-contain" />
      <span className="text-[16px] font-bold tracking-[-0.02em] text-[#120240]">
        MedSync<span className="text-[#F07D94]">IQ</span>
      </span>
    </span>
  );
}

type LandingButtonVariant = "primary" | "outline" | "outline-light";

const VARIANT_CLASS: Record<LandingButtonVariant, string> = {
  primary:
    "!border-0 !bg-[#F18095] !text-white !shadow-[0_6px_16px_rgba(241,128,149,0.35)] hover:!bg-[#EC6F87] hover:!text-white",
  outline:
    "!border-[1.5px] !border-[#D9DBE1] !bg-white !text-[#120240] !shadow-none hover:!border-[#F18095] hover:!text-[#120240]",
  "outline-light":
    "!border-[1.5px] !border-white/80 !bg-transparent !text-white !shadow-none hover:!border-white hover:!bg-white/10 hover:!text-white",
};

/**
 * antd Button styled for the landing page. `to` navigates in-app, `href`
 * is for external links, `onClick` for anything else (e.g. the contact popup).
 */
export function LandingButton({
  children, variant = "primary", to, href, onClick, arrow = false, className,
}: {
  children: ReactNode;
  variant?: LandingButtonVariant;
  to?: string;
  href?: string;
  onClick?: () => void;
  arrow?: boolean;
  className?: string;
}) {
  const navigate = useNavigate();
  return (
    <Button
      href={href}
      onClick={to ? () => navigate(to) : onClick}
      className={cn(
        "!inline-flex !items-center !justify-center !gap-2 !rounded-lg !font-bold",
        VARIANT_CLASS[variant],
        className,
      )}
    >
      {children}
      {arrow && <Icon icon="lucide:arrow-right" className="h-3.5 w-3.5" />}
    </Button>
  );
}
